<template>
  <div class="series-configuration-form" v-tooltip="{ global: true, theme: { 'background-color': '#ffffff', color: '#302E2E', padding: '15px', 'font-size': '14px', border: '1px solid #9E9EA1', 'border-radius': '0', 'box-shadow': '0 9px 12px 0 rgba(0,0,0,0.25)', 'width': '200px' } }">
    <div class="notification is-success" role="alert" v-if="successMessage !== ''" v-html="successMessage"></div>
    <div class="notification is-danger" role="alert" v-if="errorMessage !== ''" v-html="errorMessage"></div>
    <div class="columns is-multiline">
      <div class="column is-12">
        <div class="columns" v-if="hasIcon">
          <div class="column is-narrow">
            <div class="toggle-box">
              <div class="label">Standard <span v-tooltip="'ICON&trade; tools include sensors with visual feedback and data output.'"><span class="material-icons" data-localizer="ignore">info</span></span></div>
              <label class="toggle">
                <input type="checkbox" v-model="selected.icon" value="1" />
                <span class="bar"></span>
              </label>
              <div class="label"><img src="/packages/fastest/themes/fastest/images/icon-brain_words.svg" width="94" alt="ICON&trade;" /></div>
            </div>
          </div>
          <div class="column">
            <div :class="radioClass" class="form-group inline">
              <label>Signal Output:</label>
              <label class="radio"><input type="radio" v-model="selected.iconSignal" value="ana" /> Analog (ANA)</label>
              <label class="radio"><input type="radio" v-model="selected.iconSignal" value="ssr" /> Discrete (SSR)</label>
            </div>
          </div>
        </div>
      </div>
      <div class="column is-12">
        <div class="columns is-multiline filter-selects">
          <div class="column" v-for="(option) in options" :key="option.key">
            <div class="form-group">
              <label :for="option.key">{{ option.label }} <span v-if="option.key === 'seal_material' || option.key === 'seal_materials'" v-tooltip="'FasTest standard seals meet common industry requirements. Choose seals based on best chemical compatibility. Use FasTest® Urethane for high wear applications. Other seal materials available, or if questions, contact fastsales@fastestinc.com'"><span class="material-icons" data-localizer="ignore">info</span></span></label>
              <select v-if="selected[option.key] === '' && option.values.length > 1" v-model="selected[option.key]" :id="option.key">
                <option value="">{{ option.default }}</option>
                <option v-for="(value) in option.values" :key="value" :value="value">{{ value }}</option>
              </select>
              <div v-else-if="selected[option.key] === '' && option.values.length === 1" class="selected-option">{{ option.values[0] }}</div>
              <div v-else class="selected-option">{{ selected[option.key] }} <a @click="resetSelect(option.key)"><span class="material-icons" data-localizer="ignore">close</span></a></div>
            </div>
          </div>
        </div>
        <div v-if="hasSelections" style="text-align: right;"><a @click="clearSelections()" class="clear-selections"><span class="material-icons" data-localizer="ignore">close</span> Clear Selections</a></div>
      </div>
      <div class="column is-12" v-if="product !== null">
        <div class="columns is-multiline">
          <div class="column is-12 is-6-desktop">
            <div class="tabs">
              <ul class="nav">
                <li><a @click="openTab('specifications')" class="nav-link" :class="{ active: (activeTab === 'specifications') }">Specifications</a></li>
                <li v-if="isDistributor"><a @click="openTab('distributor')" class="nav-link" :class="{ active: (activeTab === 'distributor') }">Distributor Specs</a></li>
              </ul>
              <div class="tab-content">
                <div id="web" class="tab-pane" :style="{ display: (activeTab === 'specifications') ? 'block' : 'none' }" role="tabpanel">
                  <div class="columns">
                    <div class="column" :class="{ 'is-8': product.cad_files }">
                      <div class="attributes">
                        <div class="attribute">
                          <div class="label">SKU</div>
                          <div class="value" data-localizer="ignore">{{ product.sku }}</div>
                        </div>
                        <div class="attribute" v-for="(attribute, index) in product.web_attributes" :key="index">
                          <div class="label">{{ attribute.label }}</div>
                          <div class="value">{{ (typeof attribute.value === 'string') ? attribute.value : (imperialUnits) ? attribute.value.imperial : attribute.value.metric }}</div>
                        </div>
                      </div>
                    </div>
                    <div class="column is-4" v-if="product.cad_files.length">
                      <div class="files">
                        <div class="label">Download CAD:</div>
                        <div class="file" v-for="(file, index) in product.cad_files" :key="index">
                          <a :href="file.url" target="_blank">{{ file.name }}</a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div id="distributor" class="tab-pane" :style="{ display: (activeTab === 'distributor') ? 'block' : 'none' }" role="tabpanel">
                  <div class="attributes">
                    <div class="attribute" v-for="(attribute, index) in product.distributor_attributes" :key="index">
                      <div class="label">{{ attribute.label }}</div>
                      <div class="value" v-html="(typeof attribute.value === 'string') ? attribute.value : (imperialUnits) ? attribute.value.imperial : attribute.value.metric"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="column is-12 is-6-desktop">
            <div class="product-drawing">
              <a :href="'/product-pdf?product_id='+product.id" target="_blank" v-if="product !== null && product.image !== null" class="btn-light-grey">Download as PDF</a>
              <div v-if="product !== null && product.image !== null" class="product-image"><img :src="product.image" /></div>
            </div>
          </div>
          <div class="column is-12">
            <table cellpadding="5" cellspacing="0" class="products">
              <thead>
              <tr>
                <th>Product Name</th>
                <th>SKU</th>
                <th v-if="canPurchase">Price</th>
                <th>Quantity</th>
              </tr>
              </thead>
              <tbody>
              <tr>
                <td class="description">{{ product.description }}</td>
                <td data-localizer="ignore">{{ product.sku }}</td>
                <td v-if="canPurchase" class="price green">
                  <span v-if="product.available_online">${{ product.price }}</span>
                  <span v-else class="not-available">Request a Quote</span>
                </td>
                <td class="quantity"><input type="number" v-model="quantities[product.id]" min="0" /></td>
              </tr>
              <tr v-for="(companion_product, index) in product.companion_products" :key="index">
                <td class="description">{{ companion_product.description }}</td>
                <td data-localizer="ignore">{{ companion_product.sku }}</td>
                <td v-if="canPurchase" class="price">
                  <span v-if="companion_product.available_online">${{ companion_product.price }}</span>
                  <span v-else class="not-available">Request a Quote</span>
                </td>
                <td class="quantity"><input type="number" v-model="quantities[companion_product.id]" min="0" /></td>
              </tr>
              </tbody>
            </table>
            <div class="buttons">
              <div v-if="canPurchase"><button @click="addToCart()" class="btn-red" :disabled="!showAddToCart">Add to cart</button></div>
              <div><button @click="addToQuote()" class="btn-dark-grey" :disabled="!showAddToQuote">Add to Quote</button></div>
            </div>
          </div>
        </div>
      </div>
      <div class="column is-12" v-else><p><em>Select product specifications to view product details.</em></p></div>
    </div>
  </div>
</template>

<script>
/* global seriesId */

export default {
  name: 'SeriesConfiguration',
  data () {
    return {
      seriesId: seriesId,
      imperialUnits: false,
      isDistributor: false,
      canPurchase: false,
      options: [],
      hasIcon: false,
      selected: {
        icon: false,
        iconSignal: 'ana'
      },
      iconValue: 'standard',
      quantities: {},
      activeTab: 'specifications',
      showAddToCart: false,
      showAddToQuote: false,
      skuToShow: null,
      skuLoaded: false,
      product: null,
      successMessage: '',
      errorMessage: '',
      hasSelections: false
    }
  },
  created: function () {
    this.getVariables()

    let skuToShow
    (window.onpopstate = function () {
      let match
      const pl = /\+/g // Regex for replacing addition symbol with a space
      const search = /([^&=]+)=?([^&]*)/g
      const decode = function (s) { return decodeURIComponent(s.replace(pl, ' ')) }
      const query = window.location.search.substring(1)

      while ((match = search.exec(query))) {
        if (decode(match[1]) === 'sku') {
          skuToShow = decodeURIComponent(match[2])
        }
      }
    })()

    if (skuToShow !== null) {
      this.skuToShow = skuToShow
    }
  },
  computed: {
    radioClass () {
      return this.selected.icon ? '' : 'disabled'
    }
  },
  watch: {
    selected: {
      deep: true,
      handler () {
        this.hasSelections = false

        const rootObj = this
        let newValue = 'standard'
        if (this.selected.icon) {
          newValue = this.selected.iconSignal
        }
        // if we changed the icon options, reset the other selectors
        if (newValue !== this.iconValue) {
          Object.keys(rootObj.selected).forEach(function (key) {
            if (key !== 'icon' && key !== 'iconSignal') {
              rootObj.selected[key] = ''
            }
          })
          this.iconValue = newValue
        }
        this.getConfigurations()

        Object.keys(rootObj.selected).forEach(function (key) {
          if (key !== 'icon' && key !== 'iconSignal' && rootObj.selected[key] !== '') {
            rootObj.hasSelections = true
          }
        })
      }
    },
    quantities: {
      deep: true,
      handler (newValue, oldValue) {
        const rootObj = this
        rootObj.showAddToQuote = true
        rootObj.showAddToCart = true

        let quantitiesFilled = 0
        Object.keys(rootObj.quantities).forEach(function (key) {
          key = parseInt(key)
          if (parseInt(rootObj.quantities[key]) > 0) {
            quantitiesFilled++
            if (rootObj.product.id === key) {
              if (!rootObj.product.available_online) {
                rootObj.showAddToCart = false
              }
            } else {
              rootObj.product.companion_products.forEach(function (companionProduct) {
                if (companionProduct.id === key) {
                  if (!companionProduct.available_online) {
                    rootObj.showAddToCart = false
                  }
                }
              })
            }
          }
        })

        if (quantitiesFilled === 0) {
          rootObj.showAddToQuote = false
          rootObj.showAddToCart = false
        }
      }
    }
  },
  methods: {
    async getConfigurations () {
      var postData = JSON.stringify({ series_id: this.seriesId, imperial_units: this.imperialUnits, selected: this.selected })
      var rootObj = this

      fetch(
        '/get-product-configurations',
        {
          method: 'POST',
          body: postData,
          headers: { 'Content-type': 'application/json; charset=UTF-8' }
        }
      )
        .then(response => response.json())
        .then(function (json) {
          rootObj.hasIcon = json.hasIcon
          rootObj.options = json.options
          json.options.forEach(function (option) {
            if (!Object.prototype.hasOwnProperty.call(rootObj.selected, option.key)) {
              rootObj.selected[option.key] = ''
            }
          })
          rootObj.quantities = {}
          if (json.product !== null && json.product.companion_products !== null) {
            rootObj.quantities[json.product.id] = 0
            json.product.companion_products.forEach(function (companionProduct) {
              rootObj.quantities[companionProduct.id] = 0
            })
          }
          rootObj.product = json.product
        })
        .then(function () {
          if (!rootObj.skuLoaded && rootObj.skuToShow !== null) {
            rootObj.getProductBySku()
          }
        })
        .catch(function (err) {
          // eslint-disable-next-line
          console.log(err)
        })
    },
    resetSelect (key) {
      this.selected[key] = ''
      this.quantities = {}
    },
    addToQuote () {
      this.successMessage = ''
      this.errorMessage = ''

      const postData = JSON.stringify({ products: this.quantities })
      const rootObj = this

      fetch(
        '/add-to-quote',
        {
          method: 'POST',
          body: postData,
          headers: { 'Content-type': 'application/json; charset=UTF-8' }
        }
      )
        .then(response => response.json())
        .then(function (json) {
          if (json.success) {
            // reset the quantities
            Object.keys(rootObj.quantities).forEach(function (key) {
              rootObj.quantities[key] = 0
            })

            rootObj.successMessage = 'Your product(s) have been added to the quote. <a href="/quote">View Quote</a>'

            document.getElementById('quote-count').innerText = json.quoteCount
            document.getElementById('mobile-quote-count').innerText = json.quoteCount

            // scroll the user to the top
            const yOffset = -200
            const element = document.getElementById('seriesProducts')
            const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset
            window.scrollTo({ top: y, behavior: 'smooth' })
          } else {
            // add the error message
            if (json.errors.length > 0) {
              rootObj.errorMessage = json.errors.join('<br />')
            } else {
              rootObj.errorMessage = 'An unspecified error occurred, please try again.'
            }
          }
        })
        .catch(function (err) {
          // eslint-disable-next-line
          console.log(err)
        })
    },
    addToCart () {
      this.successMessage = ''
      this.errorMessage = ''

      const postData = JSON.stringify({ products: this.quantities })
      const rootObj = this

      fetch(
        '/add-to-cart',
        {
          method: 'POST',
          body: postData,
          headers: { 'Content-type': 'application/json; charset=UTF-8' }
        }
      )
        .then(response => response.json())
        .then(function (json) {
          if (json.success) {
          // reset the quantities
            Object.keys(rootObj.quantities).forEach(function (key) {
              rootObj.quantities[key] = 0
            })

            rootObj.successMessage = 'Your product(s) have been added to the cart. <a href="/cart">View Cart</a>'

            document.getElementById('cart-count').innerText = json.cartCount
            document.getElementById('mobile-cart-count').innerText = json.cartCount

            // scroll the user to the top
            const yOffset = -200
            const element = document.getElementById('seriesProducts')
            const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset
            window.scrollTo({ top: y, behavior: 'smooth' })
          } else {
            // add the error message
            if (json.errors.length > 0) {
              rootObj.errorMessage = json.errors.join('<br />')
            } else {
              rootObj.errorMessage = 'An unspecified error occurred, please try again.'
            }
          }
        })
        .catch(function (err) {
          // eslint-disable-next-line
          console.log(err)
        })
    },
    getProductBySku (sku) {
      const postData = JSON.stringify({ sku: this.skuToShow, product_series: this.seriesId })
      const rootObj = this

      fetch(
        '/get-product-by-sku',
        {
          method: 'POST',
          body: postData,
          headers: { 'Content-type': 'application/json; charset=UTF-8' }
        }
      )
        .then(response => response.json())
        .then(function (json) {
          rootObj.skuLoaded = true
          if (json.success) {
            return json.product
          } else {
            return false
          }
        })
        .then(function (product) {
          if (typeof product === 'object') {
            if (product.icon === 'standard') {
              rootObj.selected.icon = false
            } else {
              rootObj.selected.icon = true
              rootObj.selected.iconSignal = product.icon
            }

            rootObj.options.forEach(function (option) {
              const key = option.key
              if (product[key] === undefined) {
                return
              }

              // only set these selections if there is more than one available value or the UI looks weird
              if (option.values.length > 1) {
                const productValue = product[key]
                if (typeof productValue === 'string') {
                  rootObj.selected[key] = product[key]
                } else {
                  if (rootObj.imperialUnits) {
                    rootObj.selected.size = product.size.imperial
                  } else {
                    rootObj.selected.size = product.size.metric
                  }
                }
              }
            })
          }
        })
        .catch(function (err) {
          // eslint-disable-next-line
          console.log(err)
        })
    },
    getVariables () {
      const rootObj = this

      fetch('/get-series-variables')
        .then(response => response.json())
        .then(function (json) {
          rootObj.isDistributor = json.isDistributor
          rootObj.canPurchase = json.canPurchase
          rootObj.imperialUnits = json.imperialUnits
        })
        .then(function () {
          rootObj.getConfigurations()
        })
        .catch(function (err) {
          // eslint-disable-next-line
          console.log(err)
        })
    },
    openTab (newTab) {
      this.activeTab = newTab
    },
    clearSelections () {
      const rootObj = this
      Object.keys(rootObj.selected).forEach(function (key) {
        if (key !== 'icon' && key !== 'iconSignal') {
          rootObj.selected[key] = ''
        }
      })
      rootObj.skuToShow = null
    }
  }
}
</script>
